import React from 'react';
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Inbox as InboxIcon,
    Visibility as ViewIcon,
    DriveFileMove as FileIcon,
    DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import usePermissions from '../../hooks/usePermissions';
import { PERMS } from '../../config/pmPermissions';
import {
    projectsApi,
    projectDocumentsApi,
    projectDocumentFoldersApi,
} from '../../services/api';
import {
    PROJECT_DOCUMENTS_CHANGED_EVENT,
    notifyProjectDocumentsChanged,
} from '../../utils/projectDocumentsEvents';

const POLL_INTERVAL_MS = 60000;

/**
 * App-bar indicator for documents that have been uploaded (e.g. sent over
 * WhatsApp) but are not yet filed under any project. Surfaces a count badge and
 * a popover listing the unfiled documents, each with a quick "File" action.
 */
const UnfiledDocumentsIndicator = () => {
    const { has } = usePermissions();
    const canView = has(PERMS.DOCUMENTS_VIEW);
    const canFile = has(PERMS.DOCUMENTS_EDIT);
    const canDelete = has(PERMS.DOCUMENTS_DELETE);

    const [count, setCount] = React.useState(0);
    const [items, setItems] = React.useState([]);
    const [listLoading, setListLoading] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);

    const [filingDoc, setFilingDoc] = React.useState(null);
    const [projects, setProjects] = React.useState([]);
    const [folders, setFolders] = React.useState([]);
    const [selectedProjectId, setSelectedProjectId] = React.useState('');
    const [selectedFolderId, setSelectedFolderId] = React.useState('');
    const [foldersLoading, setFoldersLoading] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    const [viewingId, setViewingId] = React.useState(null);
    const [confirmDeleteDoc, setConfirmDeleteDoc] = React.useState(null);
    const [deleting, setDeleting] = React.useState(false);

    const refreshCount = React.useCallback(async () => {
        if (!canView) {
            return;
        }
        try {
            const next = await projectDocumentsApi.unallocatedCount();
            setCount(next);
        } catch (error) {
            console.error('Failed to load unfiled document count', error);
        }
    }, [canView]);

    const loadList = React.useCallback(async () => {
        if (!canView) {
            return;
        }
        setListLoading(true);
        try {
            const docs = await projectDocumentsApi.unallocated();
            setItems(Array.isArray(docs) ? docs : []);
            setCount(Array.isArray(docs) ? docs.length : 0);
        } catch (error) {
            console.error('Failed to load unfiled documents', error);
        } finally {
            setListLoading(false);
        }
    }, [canView]);

    React.useEffect(() => {
        refreshCount();
        const interval = setInterval(refreshCount, POLL_INTERVAL_MS);
        const handleChanged = () => refreshCount();
        window.addEventListener(PROJECT_DOCUMENTS_CHANGED_EVENT, handleChanged);
        return () => {
            clearInterval(interval);
            window.removeEventListener(PROJECT_DOCUMENTS_CHANGED_EVENT, handleChanged);
        };
    }, [refreshCount]);

    const handleOpen = (event) => {
        setAnchorEl(event.currentTarget);
        loadList();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const openFilingDialog = async (doc) => {
        setFilingDoc(doc);
        setSelectedProjectId('');
        setSelectedFolderId('');
        setFolders([]);
        try {
            const allProjects = await projectsApi.getAll();
            setProjects(Array.isArray(allProjects) ? allProjects : []);
        } catch (error) {
            console.error('Failed to load projects', error);
            setProjects([]);
        }
    };

    const closeFilingDialog = () => {
        setFilingDoc(null);
    };

    const handleProjectChange = async (projectId) => {
        setSelectedProjectId(projectId);
        setSelectedFolderId('');
        setFolders([]);
        if (!projectId) {
            return;
        }
        setFoldersLoading(true);
        try {
            const { folders: projectFolders } = await projectDocumentFoldersApi.list(projectId);
            setFolders(projectFolders || []);
        } catch (error) {
            console.error('Failed to load folders', error);
            setFolders([]);
        } finally {
            setFoldersLoading(false);
        }
    };

    const handleSubmitFiling = async () => {
        if (!filingDoc || !selectedProjectId || !selectedFolderId) {
            return;
        }
        setSubmitting(true);
        try {
            await projectDocumentsApi.update(filingDoc.id, {
                project_links: [
                    { project_id: selectedProjectId, folder_id: Number(selectedFolderId) },
                ],
            });
            notifyProjectDocumentsChanged(selectedProjectId);
            closeFilingDialog();
            await loadList();
        } catch (error) {
            console.error('Failed to file document', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleView = async (doc) => {
        setViewingId(doc.id);
        try {
            const { download_url: url } = await projectDocumentsApi.downloadUrl(doc.id);
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } catch (error) {
            console.error('Failed to open document', error);
        } finally {
            setViewingId(null);
        }
    };

    const handleConfirmDelete = async () => {
        if (!confirmDeleteDoc) {
            return;
        }
        setDeleting(true);
        try {
            await projectDocumentsApi.delete(confirmDeleteDoc.id);
            setConfirmDeleteDoc(null);
            await loadList();
        } catch (error) {
            console.error('Failed to delete document', error);
        } finally {
            setDeleting(false);
        }
    };

    if (!canView) {
        return null;
    }

    const open = Boolean(anchorEl);

    return (
        <>
            <Tooltip title="Unfiled documents">
                <IconButton
                    color="inherit"
                    onClick={handleOpen}
                    sx={{ ml: 1 }}
                    aria-label="unfiled documents"
                >
                    <Badge badgeContent={count} color="error" max={99}>
                        <InboxIcon />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
            >
                <Box sx={{ p: 2, pb: 1 }}>
                    <Typography variant="subtitle1">Unfiled documents</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Files uploaded without a project (e.g. sent over WhatsApp). File them so
                        they appear in the right project.
                    </Typography>
                </Box>
                <Divider />
                {listLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            Nothing to file. You&apos;re all caught up.
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding sx={{ overflowY: 'auto' }}>
                        {items.map((doc) => (
                            <ListItem
                                key={doc.id}
                                divider
                                secondaryAction={(
                                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                                        <Tooltip title="View">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleView(doc)}
                                                    disabled={viewingId === doc.id}
                                                    aria-label="view document"
                                                >
                                                    {viewingId === doc.id ? (
                                                        <CircularProgress size={16} />
                                                    ) : (
                                                        <ViewIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {canFile && (
                                            <Tooltip title="File">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openFilingDialog(doc)}
                                                        aria-label="file document"
                                                    >
                                                        <FileIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {canDelete && (
                                            <Tooltip title="Delete">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => setConfirmDeleteDoc(doc)}
                                                        aria-label="delete document"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            >
                                <ListItemText
                                    primary={doc.file_name}
                                    primaryTypographyProps={{ noWrap: true }}
                                    secondary={
                                        doc.uploaded_at
                                            ? new Date(doc.uploaded_at).toLocaleString()
                                            : null
                                    }
                                    sx={{ pr: 1 }}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Popover>

            <Dialog open={Boolean(filingDoc)} onClose={closeFilingDialog} fullWidth maxWidth="xs">
                <DialogTitle>File document</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} noWrap>
                        {filingDoc?.file_name}
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        label="Project"
                        value={selectedProjectId}
                        onChange={(event) => handleProjectChange(event.target.value)}
                        sx={{ mb: 2 }}
                    >
                        {projects.map((project) => (
                            <MenuItem key={project.id} value={String(project.id)}>
                                {project.location || `Project ${project.id}`}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        label="Folder"
                        value={selectedFolderId}
                        onChange={(event) => setSelectedFolderId(event.target.value)}
                        disabled={!selectedProjectId || foldersLoading}
                        helperText={foldersLoading ? 'Loading folders…' : ''}
                    >
                        {folders.map((folder) => (
                            <MenuItem key={folder.id} value={String(folder.id)}>
                                {folder.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFilingDialog} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitFiling}
                        disabled={!selectedProjectId || !selectedFolderId || submitting}
                    >
                        {submitting ? 'Filing…' : 'File'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(confirmDeleteDoc)}
                onClose={() => !deleting && setConfirmDeleteDoc(null)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Delete document?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This will permanently delete{' '}
                        <Typography component="span" variant="body2" color="text.primary">
                            {confirmDeleteDoc?.file_name}
                        </Typography>
                        . This action can&apos;t be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDoc(null)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UnfiledDocumentsIndicator;
